<template>
    <div>
        <div class="flex items-center justify-between">
            <div class="flex flex-1 items-center">
                <h1 class="page-header">{{ $gate.isAdmin() ? 'Clients' : 'Client' }}</h1>
                <div v-if="$gate.isAdmin()" class="ml-3 flex items-center text-gray-600">
                    <div class="text-2xl">/</div>
                    <div class="ml-2 text-xl pt-1">{{ clients.filtered.length }} of {{ clients.all.length }}</div>
                </div>
            </div>
            <div v-if="$gate.isAdmin()" class="flex flex-1 items-center justify-end gap-x-5">
                <select v-model="selectedClientId" class="mt-0 form-select max-w-fit">
                    <option value="">Select a Client</option>
                    <option v-for="client in clients.filtered" :key="client.id" :value="client.id">{{ client.name }}</option>
                </select>
                <div v-if="showFilters" class="flex items-center">
                    <filters
                        data-type="clients"
                        sort-by="name"
                        :showViewOptions="false"
                        :showClientFilter="false"
                        :showSearch="false"
                        :showFilters="false"
                        portal="no-clients-found"
                        v-on:view-change="view = $event">
                    </filters>
                    <a href='/clients/create' class="flex flex-wrap-none items-center btn btn-small btn-indigo">
                        <inline-svg name="add" classes="w-4 h-4 text-white fill-current"></inline-svg>
                        <span class="ml-2">New Client</span>
                    </a>
                </div>
            </div>
        </div>

        <div class="mt-3 w-full flex justify-between">
            <div class="flex-1">
                <div class="p-6 bg-gray-50 border rounded-sm">
                    <inline-svg v-if="loading" name="loading"></inline-svg>
                    <div v-if="! loading && selectedClientId" class="flex flex-col">
                        <div class="flex justify-between">
                            <div class="flex flex-col w-3/4">
                                <div>
                                    <h2 class="text-blue-600 text-4xl font-bold">
                                        {{ client.name }}
                                    </h2>
                                </div>
                            </div>
                            <div class="w-1/3 flex items-baseline justify-end">
                                <button
                                    @click="openEditClient(client.id)"
                                    type="button"
                                    class="flex items-center btn btn-blue focus:outline-none">
                                        <inline-svg name="edit-white"></inline-svg>
                                        Edit Client
                                </button>
                            </div>
                        </div>

                        <div class="mt-8 flex justify-between">
                            <div class="w-3/5 flex-1 mr-8">
                                <div class="flex items-center justify-center gap-x-5">
                                    <div class="flex flex-col items-center justify-center gap-y-1 rounded-full bg-teal-500 shadow-md w-32 h-32 text-white">
                                        <span class="text-4xl font-medium">{{ client.users.length }}</span>
                                        <span class="text-base font-semibold">{{ client.users.length | pluralize('User') }}</span>
                                    </div>

                                    <div class="flex flex-col items-center justify-center gap-y-1 rounded-full bg-orange-400 shadow-md w-32 h-32 text-white">
                                        <span class="text-4xl font-medium">{{ client.subsidiaries.length }}</span>
                                        <span class="text-base font-semibold">{{ client.subsidiaries.length | pluralize(['Subsidiary', 'Subsidiaries']) }}</span>
                                    </div>

                                    <div class="flex flex-col items-center justify-center gap-y-1 rounded-full bg-brand shadow-md w-32 h-32 text-white">
                                        <span class="text-4xl font-medium">{{ client.sites.length }}</span>
                                        <span class="text-base font-semibold">{{ client.sites.length | pluralize('Site') }}</span>
                                    </div>

                                    <div class="flex flex-col items-center justify-center gap-y-1 rounded-full bg-green-600 shadow-md w-32 h-32 text-white">
                                        <span class="text-4xl font-medium">{{ client.projects.length }}</span>
                                        <span class="text-base font-semibold">{{ client.projects.length | pluralize('Project') }}</span>
                                    </div>
                                </div>
                                <div class="mt-5 bg-white border border-gray-lighter rounded-sm shadow-md">
                                    <div class="p-6 flex">
                                        <div class="mt-2 flex flex-1 flex-col">
                                            <label class="module-header" for="address">Address</label>
                                            <div class="mt-5 leading-8 text-gray-700">
                                                <p class="text-2xl font-semibold uppercase">{{ client.address }}</p>
                                                <p class="text-xl uppercase">
                                                    {{ client.city }}, {{ client.state }} {{ client.postal_code }}
                                                </p>
                                                <p class="mt-1 text-lg uppercase">{{ client.country.name }}</p>
                                            </div>
                                        </div>
                                        <div v-if="client.latitude != null && client.longitude != null" class="ml-8 w-full flex-1" style="min-height: 250px">
                                            <map-component
                                                :latitude="client.latitude"
                                                :longitude="client.longitude"
                                                :zoom="6"
                                            ></map-component>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="w-2/5">
                                <div>
                                    <upcoming-sites
                                        :sites="client.sites"
                                        :show-client-info="false">
                                    </upcoming-sites>
                                </div>

                                <div class="mt-10">
                                    <active-projects
                                        :projects="client.projects"
                                        :show-client-info="false">
                                    </active-projects>
                                </div>
                            </div>
                        </div>

                        <div class="mt-12">
                            <div class="border-b border-gray-200">
                                <nav class="-mb-px flex space-x-2" aria-label="Tabs">
                                    <button
                                        v-for="(tab, slug) in enabledTabs" :key="slug"
                                        @click="selectedTab = slug"
                                        class="py-3 px-4 bg-white border border-gray-200 flex whitespace-nowrap rounded-t-lg text-base"
                                        :class="{ 'bg-gray-50 text-gray-500 font-medium hover:border-gray-200 hover:text-gray-700': slug != selectedTab, 'bg-gradient-to-br from-blue-500 to-blue-700 font-bold text-white': slug == selectedTab }">
                                            {{ tab.label }}
                                        <span
                                            v-if="tab.showCount"
                                            class="py-0.5 px-2.5 ml-3 hidden rounded-full text-sm font-medium md:inline-block"
                                            :class="{ 'bg-gray-200 text-gray-900': slug != selectedTab, 'bg-blue-800 text-white': slug == selectedTab }">
                                                {{ tab.count }}
                                        </span>
                                    </button>
                                </nav>
                            </div>
                        </div>

                        <div class="px-5 pb-5 mb-6 bg-white border-l">
                            <div class="mt-3">
                                <user-cards
                                    v-if="selectedTab == 'users'"
                                    :user-data="client.users"
                                    small-header>
                                </user-cards>

                                <subsidiary-cards
                                    v-if="selectedTab == 'subsidiaries'"
                                    :subsidiary-data="client.subsidiaries"
                                    view="table"
                                    :for-client-id="client.id"
                                    small-header>
                                </subsidiary-cards>

                                <div v-if="selectedTab == 'sites'">
                                    <div class="bg-blue-500 text-white p-6 rounded-t-lg">
                                        <div class="flex justify-around items-center text-center leading-normal">
                                            <div class="space-y-2">
                                                <p class="text-3xl lg:text-5xl font-semibold" v-text="this.sites.filtered.length"></p>
                                                <p class="text-lg font-bold">{{ sites.filtered.length | pluralize('Site') }}</p>
                                            </div>
                                            <div class="space-y-2">
                                                <p data-precision="0" :data-originalvalue="sitesTotalRemaining" class="is-currency text-3xl lg:text-5xl font-semibold">{{ formatCurrency(sitesTotalRemaining, 0) }}</p>
                                                <p class="text-lg font-bold">Total Remaining</p>
                                            </div>
                                            <div class="space-y-2">
                                                <p class="text-3xl lg:text-5xl font-semibold">{{ sitesTotalSqFt | number }}</p>
                                                <p class="text-lg font-bold">Total Sq. Ft.</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="px-4 bg-white border-l">
                                        <site-cards
                                            :site-data="client.sites"
                                            :show-create-new="false"
                                            :show-client-info="false"
                                            :per-page="100"
                                            view="table"
                                            center-cards
                                            small-header>
                                        </site-cards>
                                    </div>
                                </div>

                                <div v-if="selectedTab == 'projects'">
                                    <div class="bg-blue-500 text-white p-6 rounded-t-lg">
                                        <div class="flex justify-around items-center text-center leading-normal">
                                            <div class="space-y-2">
                                                <p class="text-3xl lg:text-5xl font-semibold" v-text="client.projects.length"></p>
                                                <p class="text-lg font-bold">{{ client.projects.length | pluralize('Project') }}</p>
                                            </div>
                                            <div class="space-y-2">
                                                <p data-precision="0" :data-originalvalue="projectsTotalRemaining" class="is-currency text-3xl lg:text-5xl font-semibold">{{ formatCurrency(projectsTotalRemaining, 0) }}</p>
                                                <p class="text-lg font-bold">Total Remaining</p>
                                            </div>
                                            <div class="space-y-2">
                                                <p class="text-3xl lg:text-5xl font-semibold">{{ projectsTotalSqFt | number }}</p>
                                                <p class="text-lg font-bold">Total Sq. Ft.</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="px-4 bg-white border-l">
                                        <project-cards
                                            :project-data="client.projects"
                                            view="table"
                                            show-completed-projects
                                            center-cards
                                            small-header
                                        ></project-cards>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="! selectedClientId">
                        <h3 class="text-2xl text-center text-gray-600">Select a client</h3>
                    </div>
                </div>
            </div>
        </div>

        <notifications classes="notification" position="bottom right"></notifications>

        <modal name="manage-users" width="60%" height="auto" :clickToClose="true" :scrollable="true">
            <manage-users
                :clientId="selectedClientId">
            </manage-users>
        </modal>

        <modal name="edit-client" width="50%" height="auto" draggable=".modal-header">
            <edit-client
                :clientId="selectedClientId"
                @client-updated="clientUpdated">
            </edit-client>
        </modal>
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    export default {
        name: 'ReoptClients',

        props: {
            clientData: {
                type: Array,
                required: true
            },

            clientId: {
                type: Number,
                required: false
            },

            openTab: {
                type: String,
                default: 'users'
            },

            showFilters: {
                type: Boolean,
                default: true
            }
        },

        data() {
            return {
                selectedClientId: this.clientId || '',
                selectedTab: this.openTab,
                errors: '',
                loading: false,

                countries: {},
                states: {},
            }
        },

        created() {
            this.$eventHub.$on('reloadClient', () => {
                this.getClient();
            });

            axios.get('/api/address-info').then((response) => {
                this.countries = response.data.countries;
                this.states = response.data.states;
            }).catch((error) => {
                console.log(error);
            });

            this.clients.all = this.clientData;
            this.clients.filtered = this.clientData;

            if (this.selectedClientId) {
                this.getClient();
            }
        },

        watch: {
            selectedClientId() {
                this.getClient();
            },

            selectedTab() {
                this.$eventHub.$emit('convert-currency');
            }
        },

        computed: {
            enabledTabs() {
                // v-for used to be paired with v-if="tab.enabled"; Vue 2 runs
                // v-for first so the list was already filtered. Same result, and
                // it does not break when the directive priority reverses in Vue 3.
                const source = this.tabs;
                return Array.isArray(source)
                    ? source.filter(entry => entry.enabled)
                    : Object.fromEntries(Object.entries(source || {}).filter(([, entry]) => entry.enabled));
            },

            ...mapState(['clients']),
            ...mapState(['sites']),

            tabs() {
                return {
                    users: {
                        label: "Users",
                        enabled: true,
                        showCount: true,
                        count: this.client?.users.length || 0
                    },
                    subsidiaries: {
                        label: "Subsidiaries",
                        enabled: true,
                        showCount: true,
                        count: this.client?.subsidiaries.length || 0
                    },
                    sites: {
                        label: "Sites",
                        enabled: true,
                        showCount: true,
                        count: this.client?.sites.length || 0
                    },
                    projects: {
                        label: "Projects",
                        enabled: true,
                        showCount: true,
                        count: this.client?.projects.length || 0
                    },
                }
            },

            client() {
                return this.clients.selectedClient;
            },

            // Kept in cents: this component renders through the global formatCurrency
            // mixin, which divides by 100.
            projectsTotalRemaining() {
                return _.sumBy(this.client.projects, 'remaining_lease_cost');
            },

            projectsTotalSqFt() {
                return _.sumBy(this.client.projects, 'required_sq_ft');
            },

            sitesTotalRemaining() {
                return _.sumBy(this.sites.filtered, 'remaining_lease_cost');
            },

            sitesTotalSqFt() {
                return _.sumBy(this.sites.filtered, 'size');
            },
        },

        methods: {
            getClient() {
                this.errors = '';
                this.loading = true;

                axios.get('/api/clients/' + this.selectedClientId).then((response) => {
                    this.clients.selectedClient = response.data;
                }).catch((error) => {
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: 'Error Pulling Client'
                    });
                }).finally(() => {
                    this.loading = false;
                });
            },

            openEditClient(clientId) {
                this.selectedClientId = clientId;

                this.$modal.show('edit-client');
            },

            clientUpdated(clientData) {
                this.clients.filtered = _.remove(this.clients.filtered, (client) => { return client.id != clientData.id });
                this.clients.filtered.unshift(clientData);
                this.clients.selectedClient = clientData;
            },
        }
    }
</script>
